import { ZodError } from 'zod';
import { ApiErrorBodySchema } from './schemas';

/**
 * Error model of the client.
 *
 * Three distinct failure kinds, because the UI reacts to them differently:
 *
 *  - `ApiRequestError`        — the server answered with its documented error
 *                               envelope. It already contains Russian operator
 *                               text; render that, never a raw code.
 *  - `ContractViolationError` — the server answered 2xx with a body that does
 *                               not match the frozen schema. This is a bug, and
 *                               it must be loud: the screen shows an explicit
 *                               "ответ сервера не соответствует контракту"
 *                               state instead of rendering `undefined`.
 *  - `NetworkError`           — the request never produced an HTTP response.
 */

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly messageRu: string;
  readonly details: readonly { field?: string | undefined; messageRu: string }[];
  readonly correlationId: string | null;

  constructor(init: {
    status: number;
    code: string;
    messageRu: string;
    details?: readonly { field?: string | undefined; messageRu: string }[];
    correlationId?: string | null;
  }) {
    super(`${init.code} (${String(init.status)}): ${init.messageRu}`);
    this.name = 'ApiRequestError';
    this.status = init.status;
    this.code = init.code;
    this.messageRu = init.messageRu;
    this.details = init.details ?? [];
    this.correlationId = init.correlationId ?? null;
  }
}

export class ContractViolationError extends Error {
  readonly endpoint: string;
  readonly issues: readonly string[];

  constructor(endpoint: string, issues: readonly string[]) {
    super(`Ответ ${endpoint} не соответствует схеме: ${issues.join('; ')}`);
    this.name = 'ContractViolationError';
    this.endpoint = endpoint;
    this.issues = issues;
  }

  get messageRu(): string {
    return (
      `Ответ сервера не соответствует контракту API (${this.endpoint}). ` +
      'Данные не показаны, чтобы не ввести оператора в заблуждение. ' +
      'Сообщите администратору и проверьте версии панели и сервера.'
    );
  }
}

export class NetworkError extends Error {
  constructor(endpoint: string, cause?: unknown) {
    super(`Сеть недоступна: ${endpoint}`);
    this.name = 'NetworkError';
    this.cause = cause;
  }

  get messageRu(): string {
    return 'Сервер недоступен. Проверьте соединение — панель повторит запрос автоматически.';
  }
}

/** Turns a zod failure into short, human-readable issue lines. */
export function describeZodIssues(error: ZodError): readonly string[] {
  return error.issues.map((issue) => {
    const path = issue.path.length === 0 ? '(корень)' : issue.path.join('.');
    return `${path}: ${issue.message}`;
  });
}

/**
 * Parses the server's error envelope. A body that is itself malformed must not
 * mask the original HTTP failure, so this degrades to a generic message rather
 * than throwing.
 */
export function toApiRequestError(status: number, body: unknown): ApiRequestError {
  const parsed = ApiErrorBodySchema.safeParse(body);
  if (parsed.success) {
    return new ApiRequestError({
      status,
      code: parsed.data.error.code,
      messageRu: parsed.data.error.messageRu,
      details: parsed.data.error.details,
      correlationId: parsed.data.error.correlationId,
    });
  }
  return new ApiRequestError({
    status,
    code: 'UNKNOWN',
    messageRu: `Сервер вернул ошибку ${String(status)} без понятного описания.`,
  });
}

/** Russian text for any thrown value, for use in an error banner. */
export function errorMessageRu(error: unknown): string {
  if (error instanceof ApiRequestError) return error.messageRu;
  if (error instanceof ContractViolationError) return error.messageRu;
  if (error instanceof NetworkError) return error.messageRu;
  if (error instanceof ZodError) {
    return `Данные не соответствуют схеме: ${describeZodIssues(error).join('; ')}`;
  }
  if (error instanceof Error && error.message !== '') return error.message;
  return 'Неизвестная ошибка.';
}

/** Field-level validation details, for mapping onto a form. */
export function fieldErrorsRu(error: unknown): Readonly<Record<string, string>> {
  if (!(error instanceof ApiRequestError)) return {};
  const result: Record<string, string> = {};
  for (const detail of error.details) {
    if (detail.field !== undefined) result[detail.field] = detail.messageRu;
  }
  return result;
}

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'UNAUTHENTICATED';
}

export function isReauthRequired(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'REAUTH_REQUIRED';
}

export function isTotpRequired(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'TOTP_REQUIRED';
}

/** Neither a re-login nor a retry helps: do not retry these. */
export function isTerminalError(error: unknown): boolean {
  if (error instanceof ContractViolationError) return true;
  if (!(error instanceof ApiRequestError)) return false;
  return error.status >= 400 && error.status < 500;
}
